import React, { useMemo, useState } from 'react';
import { Col, Row } from 'antd';
import { Input } from 'formik-antd';
import { FormikProps } from 'formik';
import { useDidUpdate } from 'rooks';

import FormItem from 'components/formik/FormItem';
import FormikSelectWithLoading from 'hocs/withLoading/FormikSelectWithLoading';
import FormikAutoCompleteWithLoading from 'hocs/withLoading/FormikAutoCompleteWithLoading';
import { useAppSelector } from 'hooks/useAppSelector';
import {
  countriesSelector,
  isCitiesLoadingSelector,
  isCountriesLoadingSelector,
  citiesSelector,
  statesSelector,
  isStatesLoadingSelector,
} from 'redux/selectors/regionSelectors';
import { BillingInfoFormValues, BillingInfoMainLocation } from '../BillingInfo';

type Props = {
  formik: FormikProps<BillingInfoFormValues>;
  mainLocation: BillingInfoMainLocation;
};

export default function BillingInfoStateForm(props: Props) {
  const { formik, mainLocation } = props;
  const locationSameAsMainValue =
    formik.values.billingLocation.locationSameAsMain;
  const [selectedCountry, setSelectedCountry] = useState<number | undefined>(
    locationSameAsMainValue
      ? mainLocation.country
      : formik.values.billingLocation.country,
  );
  const [selectedState, setSelectedState] = useState<number | undefined>(
    locationSameAsMainValue
      ? mainLocation.state
      : formik.values.billingLocation.state,
  );
  const countries = useAppSelector(countriesSelector);
  const isCountriesLoading = useAppSelector(isCountriesLoadingSelector);
  const isCitiesLoading = useAppSelector(isCitiesLoadingSelector);
  const states = useAppSelector(statesSelector);
  const isStatesLoading = useAppSelector(isStatesLoadingSelector);
  const cities = useAppSelector(citiesSelector);

  const updateSelectedValue = (
    setter: React.Dispatch<React.SetStateAction<number | undefined>>,
    mainValue?: number,
    billingValue?: number,
  ) => {
    setter(locationSameAsMainValue ? mainValue : billingValue);
  };

  useDidUpdate(() => {
    updateSelectedValue(
      setSelectedCountry,
      mainLocation.country,
      formik.values.billingLocation.country,
    );
  }, [mainLocation.country, formik.values.billingLocation.country]);

  useDidUpdate(() => {
    updateSelectedValue(
      setSelectedState,
      mainLocation.state,
      formik.values.billingLocation.state,
    );
  }, [mainLocation.state, formik.values.billingLocation.state]);

  const filteredStates = useMemo(
    () => states.filter((state) => state.countryId === selectedCountry),
    [selectedCountry, states],
  );

  const filteredCities = useMemo(
    () => cities.filter((city) => city.stateId === selectedState),
    [selectedState, cities],
  );

  const handleCountryChange = (
    value: number,
    formik: FormikProps<BillingInfoFormValues>,
  ) => {
    setSelectedCountry(value);

    formik.setFieldValue('billingLocation.city', '', false);
    formik.setFieldValue('billingLocation.state', '', false);
    formik.setFieldValue('billingLocation.internationalCityName', '', false);
    formik.setFieldValue('billingLocation.internationalStateName', '', false);
  };

  const handleStateChange = (
    stateId: number,
    formik: FormikProps<BillingInfoFormValues>,
  ) => {
    setSelectedState(stateId);

    formik.setFieldValue('billingLocation.city', '', false);
  };

  return (
    <>
      <Col xs={24} md={12}>
        <Row gutter={20}>
          <Col xs={24} md={12}>
            <FormItem
              name="billingLocation.country"
              label="Country *"
              data-id="billingInfoCountryField"
            >
              <FormikSelectWithLoading
                isLoading={isCountriesLoading}
                name="billingLocation.country"
                showSearch
                disabled={locationSameAsMainValue}
                placeholder="Select country"
                optionFilterProp="children"
                onChange={(value: number) => handleCountryChange(value, formik)}
                value={
                  locationSameAsMainValue
                    ? mainLocation.country
                    : formik.values.billingLocation.country
                }
              >
                {countries.map((country) => (
                  <FormikSelectWithLoading.Option
                    key={`select-country-${country.id}`}
                    value={country.id || -1}
                  >
                    {country.name}
                  </FormikSelectWithLoading.Option>
                ))}
              </FormikSelectWithLoading>
            </FormItem>
          </Col>

          <Col xs={24} md={12}>
            <FormItem
              name="billingLocation.state"
              label="State *"
              data-id="billingInfoStateField"
            >
              <FormikSelectWithLoading
                isLoading={isStatesLoading}
                name="billingLocation.state"
                showSearch
                placeholder="Select state"
                optionFilterProp="children"
                onChange={(value: number) => handleStateChange(value, formik)}
                disabled={
                  !formik.values.billingLocation.country ||
                  locationSameAsMainValue
                }
                value={
                  (locationSameAsMainValue
                    ? mainLocation.state
                    : formik.values.billingLocation.state) || undefined
                }
              >
                {filteredStates.map((state) => (
                  <FormikSelectWithLoading.Option
                    key={`select-state-${state.id}`}
                    value={state.id || -1}
                  >
                    {state.name}
                  </FormikSelectWithLoading.Option>
                ))}
              </FormikSelectWithLoading>
            </FormItem>
          </Col>

          <Col xs={24} md={12}>
            <FormItem
              label="ZIP code"
              data-id="newClientBillingZipField"
              name="billingLocation.zip"
            >
              <Input
                name="billingLocation.zip"
                className="full-width"
                placeholder="Enter ZIP code"
                disabled={locationSameAsMainValue}
                value={
                  locationSameAsMainValue
                    ? mainLocation.zip
                    : formik.values.billingLocation.zip
                }
                autoComplete="off"
              />
            </FormItem>
          </Col>
        </Row>
      </Col>

      <Col xs={24} md={12}>
        <Row gutter={20}>
          <Col xs={24} md={12}>
            <FormItem
              name="billingLocation.city"
              label="City *"
              data-id="billingInfoCityField"
            >
              <FormikAutoCompleteWithLoading
                isLoading={isCitiesLoading}
                name="billingLocation.city"
                filterOption
                placeholder="Enter city"
                showSearch
                disabled={
                  !formik.values.billingLocation.state ||
                  locationSameAsMainValue
                }
                value={
                  locationSameAsMainValue
                    ? mainLocation.city
                    : formik.values.billingLocation.city
                }
              >
                {filteredCities.map((city) => (
                  <FormikAutoCompleteWithLoading.Option
                    key={`billing-info-city-${city.id}`}
                    value={city.name || ''}
                  >
                    {city.name}
                  </FormikAutoCompleteWithLoading.Option>
                ))}
              </FormikAutoCompleteWithLoading>
            </FormItem>
          </Col>

          <Col xs={24} md={12}>
            <FormItem
              label="Address *"
              data-id="newClientBillingAddressField"
              name="billingLocation.address"
            >
              <Input
                name="billingLocation.address"
                placeholder="Enter address"
                disabled={locationSameAsMainValue}
                value={
                  locationSameAsMainValue
                    ? mainLocation.address
                    : formik.values.billingLocation.address
                }
                autoComplete="off"
              />
            </FormItem>
          </Col>
        </Row>
      </Col>
    </>
  );
}
